import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'
import { useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { getEntityJobs, deleteEntityJob } from '@/services/entity.service'
import { toast } from '@/utils/helpers'

import AddEditJobSlideout from './AddEditJobSlideout'

/**
 * Accounts / Jobs
 *
 * This component displays a list of jobs for a given entity. It's accessible to admins and
 * owners of the entity only.
 */
const Jobs = observer(() => {
  const { entityId } = useParams()
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'title', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showJobSlideout, setShowJobSlideout] = useState(false)
  const [editJob, setEditJob] = useState(null)

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const queryKey = ['accounts', 'jobs', entityId, sorting, pagination, searchTerm]

  // GET jobs
  const { data: jobs, isPending } = useQuery({
    queryKey,
    queryFn: () =>
      getEntityJobs({
        entityId,
        sorting,
        pagination,
        searchTerm,
      }),
    placeholderData: keepPreviousData,
  })

  // DELETE job
  const { isPending: pendingDelete, mutate: deleteJobMutation } = useMutation({
    mutationFn: (data) => deleteEntityJob({ entityId, id: data }),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Job deleted successfully')

      // Refresh accounts jobs list
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  return (
    <div className="flex flex-col gap-4">
      <div className="visible flex flex-row items-center gap-2 md:hidden">
        <Button onClick={() => setShowJobSlideout(true)}>Add Job</Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for Job"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          {searchTerm && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button className="hidden md:block" onClick={() => setShowJobSlideout(true)}>
          Add Job
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'title',
            id: 'title',
            header: ({ column }) => <SortableColumnHeader column={column} label="Title" />,
            cell: ({ row }) =>
              row.original.organization ? (
                <span className="flex items-center gap-2">
                  {row.original.title}
                  <Badge variant="secondary">Shared</Badge>
                </span>
              ) : (
                <button
                  type="button"
                  className="text-action cursor-pointer text-left hover:underline"
                  onClick={() => {
                    setEditJob(row.original)
                    setShowJobSlideout(true)
                  }}
                >
                  {row.original.title}
                </button>
              ),
          },
          {
            accessorKey: 'description',
            id: 'description',
            header: 'Description',
            cell: ({ row }) => (
              <span className="line-clamp-2">{row.original.description || ''}</span>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) =>
              row.original.organization ? null : (
                <Button
                  className="place-self-end"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditJob(row.original)
                    setShowJobSlideout(true)
                  }}
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Button>
              ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) =>
              row.original.organization ? null : (
                <ConfirmButton
                  confirmContent={`Are you sure you want to delete: ${row.original.title}?`}
                  confirmTitle="Delete Job"
                  className="place-self-end"
                  variant="destructive"
                  size="sm"
                  loading={pendingDelete}
                  onClick={() => deleteJobMutation(row.original.id)}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete job</span>
                </ConfirmButton>
              ),
            size: 80,
          },
        ]}
        data={jobs?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={jobs?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showJobSlideout && (
        <AddEditJobSlideout
          editJob={editJob}
          entityId={entityId}
          queryKey={queryKey}
          resetAndClose={() => {
            setShowJobSlideout(false)
            setEditJob(null)
            queryClient.invalidateQueries({ queryKey: ['jobs'] })
          }}
        />
      )}
    </div>
  )
})

export default Jobs
